import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "../../lib/format";

export function SettingsDialog({ open, onClose, onSaved }) {
    const [v, setV] = useState(null);
    useEffect(() => {
        if (open) api.get("/attendance/regularisation-settings").then((r) => setV({ ...r.data, recipients: (r.data.test_recipients || []).join(", ") })).catch((e) => toast.error(formatApiError(e, "Load failed")));
    }, [open]);
    if (!open || !v) return null;
    const save = async () => {
        try {
            await api.put("/attendance/regularisation-settings", {
                employee_deadline_day: Number(v.employee_deadline_day), manager_deadline_day: Number(v.manager_deadline_day),
                email_delivery_mode: v.email_delivery_mode, payroll_gate: v.payroll_gate, reminders_enabled: !!v.reminders_enabled,
                auto_accept_requests: !!v.auto_accept_requests,
                finalisation_required_from: v.finalisation_required_from || null,
                test_recipients: v.recipients.split(",").map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Settings saved.");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e, "Save failed"));
        }
    };
    const sel = (k, opts) => (
        <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`settings-${k}`}>
            {opts.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
        </select>
    );
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="month-settings-dialog">
                <DialogHeader><DialogTitle>Regularisation settings</DialogTitle><DialogDescription>Super Admin only. Every change is audited.</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Employee deadline (day of next month)</Label><Input type="number" min={1} max={28} value={v.employee_deadline_day} onChange={(e) => setV({ ...v, employee_deadline_day: e.target.value })} data-testid="settings-employee-day" /></div>
                    <div><Label className="text-xs">Approver deadline (day of next month)</Label><Input type="number" min={1} max={28} value={v.manager_deadline_day} onChange={(e) => setV({ ...v, manager_deadline_day: e.target.value })} data-testid="settings-manager-day" /></div>
                    <div><Label className="text-xs">Email delivery</Label>{sel("email_delivery_mode", [["test_only", "Test recipients only"], ["all", "Everyone"], ["off", "Off (in-app only)"]])}</div>
                    <div><Label className="text-xs">Unfinalised month in payroll</Label>{sel("payroll_gate", [["block_approval", "Block approval (override with reason)"], ["warn", "Warn only"]])}</div>
                    <div><Label className="text-xs">Finalisation required from (YYYY-MM)</Label><Input value={v.finalisation_required_from || ""} placeholder="2026-11" onChange={(e) => setV({ ...v, finalisation_required_from: e.target.value })} data-testid="settings-gate-from" /></div>
                    <div className="flex items-end gap-2 pb-2"><input type="checkbox" checked={!!v.reminders_enabled} onChange={(e) => setV({ ...v, reminders_enabled: e.target.checked })} data-testid="settings-reminders" /><Label className="text-xs">Daily reminders &amp; escalations</Label></div>
                    <div className="col-span-2 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-2.5">
                        <input id="settings-auto-accept" type="checkbox" className="mt-0.5" checked={!!v.auto_accept_requests} onChange={(e) => setV({ ...v, auto_accept_requests: e.target.checked })} data-testid="settings-auto-accept" />
                        <div><Label htmlFor="settings-auto-accept" className="text-xs cursor-pointer">Accept requests automatically</Label>
                            <p className="text-[11px] text-slate-500">On: a request updates attendance as soon as it is submitted; the reporting manager can change or reject it until the month is finalised. Off: the manager must approve first.</p></div>
                    </div>
                    <div className="col-span-2"><Label className="text-xs">Test recipients (comma separated)</Label><Input value={v.recipients} onChange={(e) => setV({ ...v, recipients: e.target.value })} data-testid="settings-test-recipients" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="settings-save-btn">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function ExtensionDialog({ open, period, employee, onClose, onSaved }) {
    const [v, setV] = useState({ employee_deadline: "", manager_deadline: "", reason: "" });
    if (!open) return null;
    const save = async () => {
        try {
            await api.post("/attendance/month-close/extension", { period, employee_id: employee?.id || null, ...v });
            toast.success("Deadline extended.");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e, "Extension failed"));
        }
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="extension-dialog">
                <DialogHeader><DialogTitle>Extend deadlines — {period}</DialogTitle><DialogDescription>{employee ? `Only for ${employee.full_name}.` : "For everyone in this month."}</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Employee deadline</Label><Input type="date" value={v.employee_deadline} onChange={(e) => setV({ ...v, employee_deadline: e.target.value })} data-testid="extension-employee-date" /></div>
                    <div><Label className="text-xs">Approver deadline</Label><Input type="date" value={v.manager_deadline} onChange={(e) => setV({ ...v, manager_deadline: e.target.value })} data-testid="extension-manager-date" /></div>
                    <div className="col-span-2"><Label className="text-xs">Reason (required)</Label><Textarea rows={2} value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} data-testid="extension-reason" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="extension-save-btn">Extend</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function FinaliseResultDialog({ result, onClose }) {
    if (!result) return null;
    const who = (x) => (x.employee_name ? `${x.employee_name} (${x.employee_code})` : x.employee_id);
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="finalise-result-dialog">
                <DialogHeader><DialogTitle>{result.finalised.length} finalised · {result.skipped.length} held</DialogTitle><DialogDescription>Held employees do not block anyone else.</DialogDescription></DialogHeader>
                <div className="max-h-[50vh] overflow-y-auto space-y-2 text-xs">
                    {result.finalised.map((f) => (
                        <div key={f.employee_id} className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5" data-testid={`finalised-${f.employee_id}`}>
                            <b>{who(f)}</b> · version {f.version} · paid days {f.paid_days}
                            {f.adjustment && <div className="text-indigo-800">Payroll adjustment {f.adjustment.id}: {f.adjustment.delta_paid_days > 0 ? "+" : ""}{f.adjustment.delta_paid_days} day(s){f.adjustment.amount != null ? ` · ₹${f.adjustment.amount}` : ""} — awaiting payroll approval</div>}
                        </div>
                    ))}
                    {result.skipped.map((s) => <div key={s.employee_id} className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid={`skipped-${s.employee_id}`}><b>{who(s)}</b> — {s.reason}</div>)}
                </div>
                <DialogFooter><Button onClick={onClose} data-testid="finalise-result-ok">Done</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function ReopenDialog({ open, period, employee, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [res, setRes] = useState(null);
    if (!open) return null;
    const go = async () => {
        try {
            const { data } = await api.post("/attendance/month-close/reopen", { period, employee_id: employee.id, reason });
            setRes(data);
            onDone();
        } catch (e) {
            toast.error(formatApiError(e, "Reopen failed"));
        }
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) { setRes(null); setReason(""); onClose(); } }}>
            <DialogContent data-testid="reopen-dialog">
                <DialogHeader><DialogTitle>Reopen {employee.full_name} — {period}</DialogTitle><DialogDescription>The employee can regularise again. Re-finalise when corrections are done.</DialogDescription></DialogHeader>
                {res ? (
                    <div className="text-xs space-y-1.5" data-testid="reopen-result">
                        <div className="font-semibold">{res.salary_already_paid ? "Salary already paid for this month." : "No closed payroll for this month."}</div>
                        {res.affected_payroll_runs.map((a) => <div key={a.run_id} className="rounded border border-slate-200 p-2">{a.run_id} ({a.status}) — {a.handling}</div>)}
                    </div>
                ) : (
                    <div><Label className="text-xs">Reason (required)</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reopen-reason" /></div>
                )}
                <DialogFooter>
                    {res ? <Button onClick={() => { setRes(null); setReason(""); onClose(); }} data-testid="reopen-done">Done</Button>
                        : <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={reason.trim().length < 10} data-testid="reopen-confirm-btn">Reopen month</Button></>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function EmailLogDialog({ open, onClose }) {
    const [d, setD] = useState(null);
    useEffect(() => {
        if (open) api.get("/notifications/email-log", { params: { limit: 200 } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e, "Load failed")));
    }, [open]);
    if (!open) return null;
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="email-log-dialog">
                <DialogHeader>
                    <DialogTitle>Attendance email log</DialogTitle>
                    <DialogDescription>Every email the workflow generated. Dry-run rows were never sent and can never be sent later.</DialogDescription>
                </DialogHeader>
                {d && (
                    <div className="space-y-2 text-xs">
                        <div className="flex flex-wrap gap-2" data-testid="email-log-totals">
                            <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1">Actually sent: <b data-testid="email-log-sent-count">{d.sent_count}</b></span>
                            {Object.entries(d.totals_by_status).map(([k, n]) => <span key={k} className="rounded border border-slate-200 bg-white px-2 py-1">{k}: <b>{n}</b></span>)}
                        </div>
                        <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded">
                            {d.items.length === 0 && <div className="p-4 text-center text-slate-400">No emails generated yet.</div>}
                            {d.items.map((r) => (
                                <div key={r.id} className="px-3 py-2 border-b border-slate-100" data-testid={`email-log-row-${r.id}`}>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.sent ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{r.sent ? "SENT" : "NOT SENT"} · {r.status}</span>
                                        <span className="font-mono">{r.to}</span>
                                        <span className="text-slate-400 ml-auto">{formatDateTime(r.at)}</span>
                                    </div>
                                    <div className="font-semibold text-slate-800 mt-0.5">{r.subject || r.kind}</div>
                                    {r.text && <div className="text-slate-600 whitespace-pre-line">{r.text}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <DialogFooter><Button variant="outline" onClick={onClose} data-testid="email-log-close">Close</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function HoursCheckDialog({ open, onClose }) {
    const [range, setRange] = useState({ from_date: "", to_date: "" });
    const [audit, setAudit] = useState(null);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [dl, setDl] = useState(false);
    if (!open) return null;
    const download = async () => {
        setDl(true);
        try {
            const res = await api.get("/attendance/reconciliation/historical-dry-run", { params: { from_date: range.from_date || undefined, to_date: range.to_date || undefined }, responseType: "blob", timeout: 600000 });
            const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "attendance-historical-dry-run.xlsx";
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = name; a.click();
            URL.revokeObjectURL(url);
            toast.success("Dry-run spreadsheet downloaded. Nothing was changed.");
        } catch (e) { toast.error(formatApiError(e, "Download failed")); }
        finally { setDl(false); }
    };
    const run = async () => {
        setBusy(true);
        try { setAudit((await api.get("/attendance/reconciliation/hours-audit", { params: range, timeout: 120000 })).data); }
        catch (e) { toast.error(formatApiError(e, "Check failed")); }
        finally { setBusy(false); }
    };
    const apply = async () => {
        const s0 = audit.summary;
        if (!window.confirm(`Recompute ${s0.rows_changing} day(s) between ${range.from_date} and ${range.to_date} for ALL employees in this range?\n\nFinalised months are skipped and every changed day is backed up so the job can be restored.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/attendance/reconciliation/hours-apply", { ...range, reason });
            toast.success(`Correction started in the background (${data.job_id}). Finalised months are skipped. Run the check again in a few minutes.`);
            setAudit(null);
        } catch (e) { toast.error(formatApiError(e, "Apply failed")); }
        finally { setBusy(false); }
    };
    const s = audit?.summary;
    return (
        <Dialog open onOpenChange={(o) => { if (!o) { setAudit(null); onClose(); } }}>
            <DialogContent className="max-w-2xl" data-testid="hours-check-dialog">
                <DialogHeader><DialogTitle>Hours check (dry run first)</DialogTitle><DialogDescription>Runs the same calculation as Apply, including the desktop-agent rules, without saving anything. Finds impossible hours (missing check-outs), shift-time and agent cut-off effects. Finalised months and frozen payroll are never changed.</DialogDescription></DialogHeader>
                <div className="flex items-end gap-2">
                    <div><Label className="text-xs">From</Label><Input type="date" value={range.from_date} onChange={(e) => setRange({ ...range, from_date: e.target.value })} data-testid="hours-from" /></div>
                    <div><Label className="text-xs">To</Label><Input type="date" value={range.to_date} onChange={(e) => setRange({ ...range, to_date: e.target.value })} data-testid="hours-to" /></div>
                    <Button onClick={run} disabled={busy || !range.from_date || !range.to_date} data-testid="hours-run-btn">Run check</Button>
                </div>
                <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900" data-testid="hours-dryrun-box">
                    <div className="font-semibold">Historical dry run (read-only spreadsheet)</div>
                    <div className="mt-1">Lists every saved pre-release day that a correction would change: saved vs proposed hours and status, the reason, and finalisation / payroll-lock impact, with real staff separated from test and inactive records. Generating it saves nothing. Leave the dates empty for the 12 months before the protection date.</div>
                    <Button size="sm" variant="outline" className="mt-2 bg-white" onClick={download} disabled={dl} data-testid="hours-dryrun-download">{dl ? "Preparing…" : "Download dry-run spreadsheet"}</Button>
                </div>
                {s && (
                    <div className="space-y-2 text-xs" data-testid="hours-summary">
                        <div className="grid grid-cols-3 gap-2">
                            {[["Days checked", s.checked], ["Over 24 h", s.over_24h], ["Missing check-out", s.open_session_past_day], ["Status changes", s.status_changes], ["Late flag changes", s.late_flag_changes], ["Early flag changes", s.early_flag_changes], ["Check-in removed", s.check_in_cleared ?? 0], ["Days changing", s.rows_changing]].map(([l, n]) => (
                                <div key={l} className="rounded border border-slate-200 p-2"><div className="text-[10px] uppercase text-slate-500">{l}</div><div className="text-lg font-bold">{n}</div></div>
                            ))}
                        </div>
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded">
                            {audit.items.slice(0, 100).map((r) => <div key={r.employee_id + r.attendance_date} className="px-2 py-1 border-b border-slate-50">{r.employee_name ? `${r.employee_name} (${r.employee_code})` : r.employee_id} · {formatDate(r.attendance_date)} · {r.old_status} → {r.new_status} · {r.old_net_hours} h → {r.new_net_hours} h · {r.flags.join(", ")}</div>)}
                        </div>
                        <Label className="text-xs">Reason to apply (required)</Label>
                        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="hours-apply-reason" />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    {s && <Button onClick={apply} disabled={busy || reason.trim().length < 10 || !s.rows_changing} data-testid="hours-apply-btn">Apply corrections</Button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
